import { useCallback, useEffect, useState } from 'react'
import { match } from 'ts-pattern'
import { Button } from '@app/components/core/button/Button'
import Row from '@app/components/layout/Row'
import useMediaQuery from '@app/util/useMediaQuery'
import {
  faArrowLeft,
  faArrowRight,
} from '@fortawesome/pro-regular-svg-icons'
import { useLocation, useSearchParams } from 'wouter'
import { pages } from '@app/util/links'
import { useClient, useQuery } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { useUserContext } from '@app/util/hooks/useUserContext'
import { PlotRegistryPageLayout } from './PlotRegistryPageLayout'
import { WelcomeStep } from './steps/WelcomeStep'
import { RegionInfoStep } from './steps/RegionInfoStep'
import { SelectInitiativeStep } from './steps/SelectInitiativeStep'
import { UploadDocumentsStep } from './steps/UploadDocumentsStep'
import { ConfirmationStep } from './steps/ConfirmationStep'
import type { ActivePlotRegistryItem, RawPlotRegistryItem } from 'functions/app/plot-registry/list-active'
import type { PlotRegistryUserInitiative } from 'functions/app/plot-registry/list-user-initiatives'

const plotRegistrySteps = {
  welcome: 'welcome',
  regionInfo: 'regionInfo',
  selectInitiative: 'selectInitiative',
  uploadDocuments: 'uploadDocuments',
  confirmation: 'confirmation',
} as const
type PlotRegistryStep = (typeof plotRegistrySteps)[keyof typeof plotRegistrySteps]

export const PlotRegistryFlow = () => {
  const isMobile = useMediaQuery()
  const client = useClient()
  const [_, navigate] = useLocation()
  const { showFailToast } = useToast()
  const { user } = useUserContext()

  const [step, setStep] = useState<PlotRegistryStep>(plotRegistrySteps.welcome)
  const [selectedPlotRegistry, setSelectedPlotRegistry] =
    useState<ActivePlotRegistryItem | null>(null)
  const [selectedInitiative, setSelectedInitiative] =
    useState<PlotRegistryUserInitiative | null>(null)
  const [applicationId, setApplicationId] = useState<number | null>(null)
  const [consentGiven, setConsentGiven] = useState(false)
  const [allDocumentsUploaded, setAllDocumentsUploaded] = useState(false)

  const [searchParams] = useSearchParams()
  const preselectedId = searchParams.get('plotRegistryId')

  const { data: activePlotRegistriesRaw } = useQuery(
    'list-active-plot-registries',
    {},
  )

  useEffect(() => {
    if (!preselectedId || selectedPlotRegistry || !activePlotRegistriesRaw) {
      return
    }
    const items = (activePlotRegistriesRaw as RawPlotRegistryItem[])
      .filter((k) => k.applicationWindows?.length > 0)
      .map((k) => ({ ...k, applicationWindow: k.applicationWindows[0] }))
    const match = items.find((k) => k.id === Number(preselectedId))
    if (match) {
      setSelectedPlotRegistry(match)
      setStep(plotRegistrySteps.regionInfo)
    }
  }, [preselectedId, activePlotRegistriesRaw, selectedPlotRegistry])

  const handleClose = useCallback(() => {
    navigate(pages.home)
  }, [navigate])

  const handleNext = useCallback(async () => {
    await match(step)
      .with(plotRegistrySteps.welcome, () => {
        if (selectedPlotRegistry) {
          setStep(plotRegistrySteps.regionInfo)
        }
      })
      .with(plotRegistrySteps.regionInfo, () => {
        setStep(plotRegistrySteps.selectInitiative)
      })
      .with(plotRegistrySteps.selectInitiative, async () => {
        if (!selectedInitiative || !selectedPlotRegistry) {
          return
        }

        try {
          if (selectedInitiative.hasExistingApplication) {
            setApplicationId(selectedInitiative.existingApplicationId ?? null)
            setStep(plotRegistrySteps.uploadDocuments)
          } else {
            const id = await client.call('create-plot-registry-application', {
              plotRegistryId: selectedPlotRegistry.id,
              initiativeId: selectedInitiative.id,
            })
            setApplicationId(id as number)
            setStep(plotRegistrySteps.uploadDocuments)
          }
        } catch {
          showFailToast()
        }
      })
      .with(plotRegistrySteps.uploadDocuments, async () => {
        if (!applicationId) {
          return
        }
        try {
          await client.call('submit-plot-registry-application', {
            applicationId,
          })
          setStep(plotRegistrySteps.confirmation)
        } catch {
          showFailToast()
        }
      })
      .with(plotRegistrySteps.confirmation, () => {
        handleClose()
      })
      .exhaustive()
  }, [
    step,
    selectedPlotRegistry,
    selectedInitiative,
    applicationId,
    client,
    showFailToast,
    handleClose,
  ])

  const handlePrevious = useCallback(() => {
    match(step)
      .with(plotRegistrySteps.regionInfo, () => {
        setStep(plotRegistrySteps.welcome)
      })
      .with(plotRegistrySteps.selectInitiative, () => {
        setStep(plotRegistrySteps.regionInfo)
      })
      .with(plotRegistrySteps.uploadDocuments, () => {
        setStep(plotRegistrySteps.selectInitiative)
      })
      .otherwise(() => {})
  }, [step])

  const isNextEnabled = match(step)
    .with(plotRegistrySteps.welcome, () => !!selectedPlotRegistry)
    .with(plotRegistrySteps.regionInfo, () => true)
    .with(plotRegistrySteps.selectInitiative, () => !!selectedInitiative)
    .with(plotRegistrySteps.uploadDocuments, () => consentGiven && allDocumentsUploaded)
    .with(plotRegistrySteps.confirmation, () => true)
    .exhaustive()

  const showPrevious = match(step)
    .with(plotRegistrySteps.welcome, () => false)
    .with(plotRegistrySteps.confirmation, () => false)
    .otherwise(() => true)

  const nextLabel = match(step)
    .with(plotRegistrySteps.confirmation, () => 'Sluit dit formulier')
    .otherwise(() => 'Volgende')

  const nextIcon = match(step)
    .with(plotRegistrySteps.confirmation, (): undefined => undefined)
    .otherwise(() => faArrowRight)

  return (
    <PlotRegistryPageLayout title="Kaartenbak" onClose={handleClose}>
      {match(step)
        .with(plotRegistrySteps.welcome, () => (
          <WelcomeStep
            selectedPlotRegistry={selectedPlotRegistry}
            onSelect={setSelectedPlotRegistry}
          />
        ))
        .with(plotRegistrySteps.regionInfo, () =>
          selectedPlotRegistry ? (
            <RegionInfoStep plotRegistryId={selectedPlotRegistry.id} />
          ) : null,
        )
        .with(plotRegistrySteps.selectInitiative, () =>
          selectedPlotRegistry && user?.id ? (
            <SelectInitiativeStep
              plotRegistryId={selectedPlotRegistry.id}
              userId={user.id}
              selectedInitiative={selectedInitiative}
              onSelect={setSelectedInitiative}
            />
          ) : null,
        )
        .with(plotRegistrySteps.uploadDocuments, () =>
          applicationId && selectedPlotRegistry ? (
            <UploadDocumentsStep
              applicationId={applicationId}
              plotRegistryId={selectedPlotRegistry.id}
              municipalityName={
                selectedPlotRegistry.municipality?.name ?? ''
              }
              consentGiven={consentGiven}
              onConsentChange={setConsentGiven}
              onAllDocumentsUploadedChange={setAllDocumentsUploaded}
            />
          ) : null,
        )
        .with(plotRegistrySteps.confirmation, () =>
          selectedPlotRegistry ? (
            <ConfirmationStep plotRegistryId={selectedPlotRegistry.id} />
          ) : null,
        )
        .exhaustive()}

      <Row
        alignItems="center"
        style={{
          justifyContent: 'space-between',
          flexDirection: 'row-reverse',
          position: 'fixed',
          width: isMobile ? '100%' : '85%',
          paddingInline: isMobile ? '5rem' : 0,
          maxWidth: '77.5rem',
          bottom: '1.5rem',
        }}
      >
        <Button
          label={nextLabel}
          icon={nextIcon}
          disabled={!isNextEnabled}
          onClick={() => void handleNext()}
        />
        {showPrevious && (
          <Button
            label="Terug"
            icon={faArrowLeft}
            iconPosition="left"
            variant="secondary"
            onClick={handlePrevious}
          />
        )}
      </Row>
    </PlotRegistryPageLayout>
  )
}
